import { useState, useEffect, useRef, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, EyeOff, Sun, Moon, Calendar, MapPin, X, Globe, ListChecks, Landmark, Users } from "lucide-react";
import { login, type LoginResponse } from "@/services/auth";
import { useAuth } from "@/hooks/useAuth";
import axios from "axios";
import { axiosClient } from "@/lib/axiosClient";
import { useTranslation } from "react-i18next"; // 1. Import i18next

export default function LoginPage(): JSX.Element | null {
    const { t, i18n } = useTranslation(['auth', 'public']); // 2. Gunakan namespace auth & public

    // === REF UNTUK AUTOFOCUS ===
    const emailInputRef = useRef<HTMLInputElement>(null);

    // === STATE FORM ===
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false); // Loading tombol login

    // === STATE TAMPILAN ===
    const [darkMode, setDarkMode] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [sending, setSending] = useState(false);

    // === HOOKS ===
    const { setAuthSuccess, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // === SINKRONISASI TEMA DARI NAVIGATION (LOCAL STORAGE) ===
    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");

        // Hanya aktifkan dark mode jika ada "dark" di localStorage
        if (savedTheme === "dark") {
            setDarkMode(true);
            document.documentElement.classList.add("dark");
        } else {
            // Default selalu light
            setDarkMode(false);
            document.documentElement.classList.remove("dark");
        }
    }, []);
    // useEffect(() => {
    //     const savedTheme = localStorage.getItem("theme");
    //     const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

    //     if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
    //         setDarkMode(true);
    //         document.documentElement.classList.add("dark");
    //     } else {
    //         setDarkMode(false);
    //         document.documentElement.classList.remove("dark");
    //     }
    // }, []);

    // === AUTO FOCUS KE EMAIL SAAT HALAMAN DIMUAT ===
    useEffect(() => {
        if (!authLoading && !user) {
            // Memberikan sedikit jeda agar animasi halaman selesai sebelum fokus
            setTimeout(() => {
                emailInputRef.current?.focus();
            }, 100);
        }
    }, [authLoading, user]);

    // === HANDLER TOGGLE TEMA ===
    const toggleTheme = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        if (newMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    };

    // === 1. AUTO REDIRECT (PENTING) ===
    useEffect(() => {
        if (!authLoading && user) {
            navigate("/dashboard", { replace: true });
        }
    }, [user, authLoading, navigate]);

    // === 2. LOGIN HANDLER ===
    const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 1. Jalankan Login untuk dapat Token
            const response: LoginResponse = await login(email, password);

            // 2. Simpan token ke localStorage & Axios Headers SEGERA
            // Ini supaya request /me di bawah tidak 401 Unauthorized
            localStorage.setItem("access_token", response.access_token);
            axiosClient.defaults.headers.common["Authorization"] = `Bearer ${response.access_token}`;

            // 3. AMBIL DATA LENGKAP (User + Participant + PIC info)
            // Pastikan endpoint /me di Laravel sudah me-load relasi participant
            const { data: fullUserResponse } = await axiosClient.get("/me");

            // Terkadang response /me dibungkus objek { data: ... } atau { user: ... }
            // Sesuaikan dengan isi response backend Anda
            const fullUserData = fullUserResponse.data || fullUserResponse.user || fullUserResponse;

            // 4. Set state Auth dengan data LENGKAP
            // Sekarang StepGuard tidak akan 'bingung' karena data pic_id sudah ada sejak awal
            setAuthSuccess(fullUserData, response.access_token);

            toast.success(t('auth:login.success_msg', { name: fullUserData.name }));

            // 5. Navigasi
            const origin = (location.state as { from?: { pathname: string } })?.from?.pathname || "/dashboard";
            navigate(origin, { replace: true });

        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message = (err.response?.data as { message?: string })?.message ?? t('auth:login.error_fail');
                toast.error(message);
            } else {
                toast.error(t('auth:login.error_conn'));
            }
            // Hapus token jika gagal ambil data lengkap untuk keamanan
            localStorage.removeItem("access_token");
        } finally {
            setLoading(false);
        }
    };

    // === FORGOT PASSWORD HANDLER ===
    const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSending(true);
        try {
            await axiosClient.post("/forgot-password", { email: forgotEmail });
            toast.success(t('auth:forgot_password.success_msg'));
            setShowForgot(false);
        } catch {
            toast.error(t('auth:forgot_password.error_msg'));
        } finally {
            setSending(false);
        }
    };

    // === LANGUAGE TOGGLE HANDLER ===
    const toggleLanguage = (): void => {
        const newLang = i18n.language === 'id' ? 'en' : 'id';
        i18n.changeLanguage(newLang);
    };

    // === TAMPILAN LOADING AWAL ===
    if (authLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-300 transition-colors">
                <div className="relative flex items-center justify-center mb-6">
                    <div className="h-14 w-14 border-4 border-slate-200 dark:border-slate-800 border-t-blue-600 dark:border-t-blue-500 rounded-full animate-spin"></div>
                </div>
                <p className="text-lg font-medium tracking-wide animate-pulse">
                    {t('auth:login.checking_session')}
                </p>
            </div>
        );
    }

    if (user) return null;

    return (
        <div
            className={`min-h-screen flex items-center justify-center relative overflow-hidden transition-colors duration-700 p-4 sm:p-8 ${darkMode
                ? "bg-slate-950 text-slate-100"
                : "bg-slate-50 text-slate-800"
                }`}
        >
            {/* Background Decorative */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
                <div className={`absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full blur-[120px] ${darkMode ? 'bg-blue-900/20' : 'bg-blue-200/40'}`}></div>
                <div className={`absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] rounded-full blur-[120px] ${darkMode ? 'bg-indigo-900/20' : 'bg-indigo-200/40'}`}></div>
            </div>

            {/* MAIN CARD CONTAINER */}
            <div className={`z-10 w-full max-w-[1050px] flex flex-col md:flex-row min-h-[600px] rounded-[24px] overflow-hidden shadow-[0_15px_60px_-15px_rgba(0,0,0,0.1)] transition-all duration-500 backdrop-blur-xl relative ${darkMode
                ? "bg-slate-900/90 border border-slate-800"
                : "bg-white/90 border border-slate-100"
                }`}
            >
                {/* === LEFT COLUMN: FORM === */}
                <div className="w-full md:w-1/2 p-8 sm:p-12 flex flex-col relative justify-center z-10">

                    {/* Header Left (Logo) */}
                    <div className="flex items-center gap-3 mb-10 cursor-pointer" onClick={() => navigate("/")}>
                        <img src="/konaspi.png" alt="Logo" className="h-10 w-auto object-contain" />
                        <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-white">KONASPI XII</span>
                    </div>

                    {/* Welcome Text - Translated */}
                    <div className="mb-8">
                        <h1 className="text-2xl sm:text-3xl font-semibold mb-2 text-slate-800 dark:text-slate-100 leading-tight">
                            {t('auth:login.welcome_heading')}<br />{t('auth:login.welcome_subheading')}
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {t('auth:login.subtitle')}
                        </p>
                    </div>

                    {/* Form Login */}
                    <form onSubmit={handleLogin} className="space-y-6">

                        {/* Email Input - Translated */}
                        <div className="relative group">
                            <label className="block text-xs font-medium text-slate-400 dark:text-slate-500 mb-1">
                                {t('auth:login.email_label')}
                            </label>
                            <input
                                ref={emailInputRef} // Autofocus di sini
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('auth:login.email_placeholder')}
                                required
                                className={`w-full bg-transparent border-0 border-b-[1.5px] pb-2 text-sm focus:ring-0 focus:outline-none transition-colors duration-300 ${darkMode
                                    ? "border-slate-700 text-slate-100 placeholder-slate-600 focus:border-blue-400"
                                    : "border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                                    }`}
                            />
                        </div>

                        {/* Password Input - Translated */}
                        <div className="relative group">
                            <label className="block text-xs font-medium text-slate-400 dark:text-slate-500 mb-1">
                                {t('auth:login.password_label')}
                            </label>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••••"
                                required
                                className={`w-full bg-transparent border-0 border-b-[1.5px] pb-2 pr-10 text-sm focus:ring-0 focus:outline-none transition-colors duration-300 tracking-widest ${darkMode
                                    ? "border-slate-700 text-slate-100 placeholder-slate-600 focus:border-blue-400"
                                    : "border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className={`absolute right-0 bottom-2 p-1 rounded-md transition-colors duration-200 ${darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>

                        {/* Extra Actions: Forgot Password - Translated */}
                        <div className="flex justify-between items-center pt-2">
                            <div className="flex items-center gap-2"></div>
                            <button
                                type="button"
                                onClick={() => setShowForgot(true)}
                                className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            >
                                {t('auth:login.forgot_password_link')}
                            </button>
                        </div>

                        {/* Action Buttons - Translated */}
                        <div className="flex items-center gap-4 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="min-w-[120px] h-[44px] flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg shadow-lg shadow-blue-500/20 transition-all duration-300 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    </span>
                                ) : (
                                    t('auth:login.btn_login')
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => navigate("/registration/convention")}
                                className={`min-w-[120px] font-medium py-2.5 px-6 rounded-lg border transition-all duration-300 active:scale-95 ${darkMode
                                    ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                                    }`}
                            >
                                {t('auth:login.btn_register')}
                            </button>
                        </div>
                    </form>

                    {/* Footer Info - Translated */}
                    <div className="mt-auto pt-10 text-xs text-slate-400 dark:text-slate-500 space-y-1">
                        <p>{t('auth:terms.agreement')}</p>
                        <p className="text-blue-600 dark:text-blue-400 font-medium cursor-pointer">
                            {t('auth:terms.policy_link')}
                        </p>

                        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 opacity-80">
                            <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-blue-500" /> <span>{t('public:footer.event_conference')}</span></div>
                            <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-blue-500" /> <span>{t('public:hero.conference.location')}</span></div>
                        </div>
                    </div>
                </div>

                {/* === RIGHT COLUMN: INFORMATIVE PRESENTATION === */}
                <div className={`hidden md:flex w-1/2 relative flex-col justify-between overflow-hidden border-l ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-[#fafafa] border-slate-100'}`}>

                    {/* Dot Pattern Background */}
                    <div
                        className="absolute inset-0 opacity-[0.05] dark:opacity-[0.08]"
                        style={{
                            backgroundImage: 'radial-gradient(currentColor 2px, transparent 2px)',
                            backgroundSize: '32px 32px'
                        }}
                    ></div>

                    {/* Glow Brand Colors (Blue & Yellow) */}
                    <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-[80px] pointer-events-none"></div>

                    {/* Top Toggles Container */}
                    <div className="absolute top-6 right-6 z-30 flex items-center gap-2">
                        <button
                            onClick={toggleLanguage}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md border transition-all text-xs font-bold ${darkMode ? 'bg-slate-800/80 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white shadow-sm'}`}
                        >
                            <Globe className="h-3.5 w-3.5" />
                            <span className="uppercase">{i18n.language}</span>
                        </button>

                        <button
                            onClick={toggleTheme}
                            className={`p-1.5 rounded-full backdrop-blur-md border transition-all ${darkMode ? 'bg-slate-800/80 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white shadow-sm'}`}
                        >
                            {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                    </div>

                    {/* Content: Info Cards - Translated */}
                    <div className="relative z-20 px-10 pt-20 pb-6 flex-1 flex flex-col justify-center space-y-4">

                        {/* Card 1 */}
                        <div className={`p-5 rounded-2xl border transition-colors shadow-sm ${darkMode ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-100 hover:border-blue-100'}`}>
                            <div className="flex items-start gap-4">
                                <div className={`p-2.5 rounded-lg shrink-0 ${darkMode ? 'bg-slate-700 text-blue-400' : 'bg-blue-50 text-[#0f285c]'}`}>
                                    <ListChecks className="h-5 w-5" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm mb-1.5 ${darkMode ? 'text-slate-100' : 'text-[#0f285c]'}`}>
                                        {t('auth:info_panel.track_title')}
                                    </h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {t('auth:info_panel.track_desc')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Card 2 */}
                        <div className={`p-5 rounded-2xl border transition-colors shadow-sm ${darkMode ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-100 hover:border-blue-100'}`}>
                            <div className="flex items-start gap-4">
                                <div className={`p-2.5 rounded-lg shrink-0 ${darkMode ? 'bg-slate-700 text-blue-400' : 'bg-blue-50 text-[#0f285c]'}`}>
                                    <Landmark className="h-5 w-5" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm mb-1.5 ${darkMode ? 'text-slate-100' : 'text-[#0f285c]'}`}>
                                        {t('auth:info_panel.lptk_title')}
                                    </h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {t('auth:info_panel.lptk_desc')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Card 3 */}
                        <div className={`p-5 rounded-2xl border transition-colors shadow-sm ${darkMode ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-100 hover:border-blue-100'}`}>
                            <div className="flex items-start gap-4">
                                <div className={`p-2.5 rounded-lg shrink-0 ${darkMode ? 'bg-slate-700 text-blue-400' : 'bg-blue-50 text-[#0f285c]'}`}>
                                    <Users className="h-5 w-5" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm mb-1.5 ${darkMode ? 'text-slate-100' : 'text-[#0f285c]'}`}>
                                        {t('auth:info_panel.convention_title')}
                                    </h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {t('auth:info_panel.convention_desc')}
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Bottom Banner Section - Translated */}
                    <div className="relative z-20 w-full px-8 pb-8 pt-4">
                        <div className="bg-[#1b43bc] rounded-2xl p-6 text-center shadow-lg relative overflow-hidden">
                            {/* Decorative banner elements */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10"></div>

                            <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-2 tracking-tight">
                                {t('auth:info_panel.banner_title')}
                            </h2>
                            <p className="text-white/90 text-sm font-medium mb-3">
                                {t('auth:info_panel.banner_subtitle')}
                            </p>
                            <p className="text-white/70 text-xs italic tracking-wide">
                                {t('auth:info_panel.banner_tags')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* === MODAL FORGOT PASSWORD - Translated === */}
            {showForgot && (
                <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 backdrop-blur-sm p-4 text-left transition-opacity">
                    <div className={`rounded-2xl p-8 w-full max-w-sm shadow-2xl relative border transform transition-all animate-in fade-in zoom-in-95 duration-300 ${darkMode ? "bg-slate-900 border-slate-700/50" : "bg-white border-white"}`}>
                        <button
                            onClick={() => setShowForgot(false)}
                            className={`absolute top-4 right-4 p-1.5 rounded-md transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-400 hover:bg-slate-100'}`}
                        >
                            <X className="h-5 w-5" />
                        </button>

                        <div className="mb-6">
                            <h2 className={`text-xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                                {t('auth:forgot_password.modal_title')}
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {t('auth:forgot_password.modal_desc')}
                            </p>
                        </div>

                        <form onSubmit={handleForgotPassword} className="space-y-5">
                            <div className="relative group">
                                <label className="block text-xs font-medium text-slate-400 dark:text-slate-500 mb-1">
                                    {t('auth:forgot_password.email_label')}

                                    <input
                                        type="email"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        required
                                        placeholder={t('auth:forgot_password.email_placeholder')}
                                        className={`w-full bg-transparent border-0 border-b-[1.5px] pb-2 text-sm focus:ring-0 focus:outline-none transition-colors duration-300 mt-1 ${darkMode
                                            ? "border-slate-700 text-slate-100 placeholder-slate-600 focus:border-blue-400"
                                            : "border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                                            }`}
                                    />
                                </label>
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition-all duration-300 shadow-md disabled:opacity-50 active:scale-95"
                            >
                                {sending ? t('auth:forgot_password.btn_sending') : t('auth:forgot_password.btn_send')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}